/**
 * Feature Flag System — Resolution Engine
 *
 * Priority-based flag resolution:
 * 1. User override (highest)
 * 2. Role override
 * 3. Program override
 * 4. Global enabled + rollout %
 *
 * Supports: boolean flags, percentage rollout, A/B variants,
 * per-tenant/role/user overrides.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface FeatureFlag {
  key: string;
  description: string;
  enabled: boolean;
  rolloutPercentage: number; // 0-100
  variant?: 'A' | 'B' | null; // For A/B testing
  programOverrides: Record<string, boolean>;
  roleOverrides: Record<string, boolean>;
  userOverrides: Record<string, boolean>;
  defaultOnFailure: boolean; // Fallback: true = ON if load fails, false = OFF
  category: FlagCategory;
  createdAt: string;
  updatedAt: string;
}

export type FlagCategory = 'feature' | 'experiment' | 'ops' | 'ui' | 'rollout';

export interface FlagResolutionContext {
  userId: string | null;
  role: string | null;
  programId: string | null;
}

export interface FlagResolutionResult {
  enabled: boolean;
  source: 'user-override' | 'role-override' | 'program-override' | 'global' | 'rollout' | 'disabled' | 'default';
  variant?: 'A' | 'B' | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Resolution Engine
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deterministic hash for rollout percentage.
 * Uses userId + flagKey to ensure consistent assignment.
 */
function hashForRollout(userId: string, flagKey: string): number {
  const str = `${userId}:${flagKey}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % 100;
}

/**
 * Resolve whether a feature flag is enabled for the given context.
 */
export function resolveFlag(flag: FeatureFlag, ctx: FlagResolutionContext): FlagResolutionResult {
  // Priority 1: User override
  if (ctx.userId && flag.userOverrides[ctx.userId] !== undefined) {
    return {
      enabled: flag.userOverrides[ctx.userId],
      source: 'user-override',
      variant: flag.variant,
    };
  }

  // Priority 2: Role override
  if (ctx.role && flag.roleOverrides[ctx.role] !== undefined) {
    return {
      enabled: flag.roleOverrides[ctx.role],
      source: 'role-override',
      variant: flag.variant,
    };
  }

  // Priority 3: Program override
  if (ctx.programId && flag.programOverrides[ctx.programId] !== undefined) {
    return {
      enabled: flag.programOverrides[ctx.programId],
      source: 'program-override',
      variant: flag.variant,
    };
  }

  // Priority 4: Global enabled check
  if (!flag.enabled) {
    return { enabled: false, source: 'disabled', variant: null };
  }

  // Priority 5: Rollout percentage
  if (flag.rolloutPercentage < 100) {
    if (!ctx.userId) {
      // No user context — can't do deterministic rollout
      return { enabled: false, source: 'rollout', variant: null };
    }
    const userHash = hashForRollout(ctx.userId, flag.key);
    const enabled = userHash < flag.rolloutPercentage;
    return { enabled, source: 'rollout', variant: enabled ? flag.variant : null };
  }

  // Fully enabled
  return { enabled: true, source: 'global', variant: flag.variant };
}

/**
 * Get A/B variant for a flag (deterministic per user).
 */
export function getVariant(userId: string, flagKey: string): 'A' | 'B' {
  const hash = hashForRollout(userId, flagKey);
  return hash < 50 ? 'A' : 'B';
}

// ─────────────────────────────────────────────────────────────────────────────
// Storage & Cache
// ─────────────────────────────────────────────────────────────────────────────

const FLAGS_CACHE_KEY = 'epprem-feature-flags';
const FLAGS_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

interface CachedFlags {
  flags: Record<string, FeatureFlag>;
  cachedAt: number;
}

export function getCachedFlags(): Record<string, FeatureFlag> | null {
  try {
    const raw = localStorage.getItem(FLAGS_CACHE_KEY);
    if (!raw) return null;
    const cached: CachedFlags = JSON.parse(raw);
    if (Date.now() - cached.cachedAt > FLAGS_CACHE_TTL) {
      localStorage.removeItem(FLAGS_CACHE_KEY);
      return null;
    }
    return cached.flags;
  } catch {
    return null;
  }
}

export function setCachedFlags(flags: Record<string, FeatureFlag>): void {
  try {
    const cached: CachedFlags = { flags, cachedAt: Date.now() };
    localStorage.setItem(FLAGS_CACHE_KEY, JSON.stringify(cached));
  } catch { /* ignore */ }
}

export function clearFlagsCache(): void {
  try { localStorage.removeItem(FLAGS_CACHE_KEY); } catch { /* ignore */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// Default Flags (Hardcoded Fallbacks)
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_FLAGS: Record<string, FeatureFlag> = {
  workflow_v2: {
    key: 'workflow_v2',
    description: 'New workflow builder interface',
    enabled: false,
    rolloutPercentage: 0,
    variant: null,
    programOverrides: {},
    roleOverrides: {},
    userOverrides: {},
    defaultOnFailure: false,
    category: 'feature',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  dark_mode: {
    key: 'dark_mode',
    description: 'Dark mode toggle in user preferences',
    enabled: true,
    rolloutPercentage: 100,
    variant: null,
    programOverrides: {},
    roleOverrides: {},
    userOverrides: {},
    defaultOnFailure: true,
    category: 'ui',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  theme_presets: {
    key: 'theme_presets',
    description: 'Theme preset system with preview',
    enabled: true,
    rolloutPercentage: 100,
    variant: null,
    programOverrides: {},
    roleOverrides: {},
    userOverrides: {},
    defaultOnFailure: true,
    category: 'ui',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  white_label: {
    key: 'white_label',
    description: 'Multi-tenant white-label branding',
    enabled: true,
    rolloutPercentage: 100,
    variant: null,
    programOverrides: {},
    roleOverrides: {},
    userOverrides: {},
    defaultOnFailure: false,
    category: 'feature',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  module_themes: {
    key: 'module_themes',
    description: 'Per-module theme color overrides',
    enabled: true,
    rolloutPercentage: 100,
    variant: null,
    programOverrides: {},
    roleOverrides: {},
    userOverrides: {},
    defaultOnFailure: false,
    category: 'feature',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  export_import_theme: {
    key: 'export_import_theme',
    description: 'Export and import theme configurations',
    enabled: true,
    rolloutPercentage: 100,
    variant: null,
    programOverrides: {},
    roleOverrides: {},
    userOverrides: {},
    defaultOnFailure: true,
    category: 'feature',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  patient360_redesign: {
    key: 'patient360_redesign',
    description: 'Redesigned Patient Details dashboard',
    enabled: false,
    rolloutPercentage: 0,
    variant: null,
    programOverrides: {},
    roleOverrides: {},
    userOverrides: {},
    defaultOnFailure: false,
    category: 'rollout',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  ai_assistance: {
    key: 'ai_assistance',
    description: 'AI-powered form auto-fill and suggestions',
    enabled: false,
    rolloutPercentage: 0,
    variant: null,
    programOverrides: {},
    roleOverrides: { SuperAdmin: true },
    userOverrides: {},
    defaultOnFailure: false,
    category: 'experiment',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Debug Mode
// ─────────────────────────────────────────────────────────────────────────────

export function enableDebugMode(flags: Record<string, FeatureFlag>): void {
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).__featureFlags = flags;
  }
}
